import React from 'react';
import { FormattedMessage } from 'react-intl';
import { getApiPermissionName } from '../../../configs/ApiPermissionsMap';

import { useIdelinkTheme } from '../../../configs/IdeLinkTheme';
import { ItemState } from '../../../services/commons/ItemState';
import { AdminPermission } from '../../../services/commons/Me';
import {
    AdminPermissions,
    AdminPermissionsOnModule,
} from '../../../services/commons/Permissions/AdminPermissions';
import {
    ContentPermission,
    UserPermissions,
    UserPermissionsOnContent,
} from '../../../services/commons/Permissions/ContentPermission';
import { PermissionsAbilityHelper } from '../../../services/commons/Permissions/PermissionsAbilityHelper';
import { PermissionType } from '../../../services/commons/Permissions/PermissionType';
import { TabPermissionItem } from '../../BackUsers/Pages/components/PermissionsTreeContainer/PermissionsTreeContainer';

export interface UserPermissionsOnCommunicationItem extends UserPermissionsOnContent {
    readonly canEditPrice?: boolean;
    readonly canEditBeforePromoPrice?: boolean;
    readonly canEditMinimalCount?: boolean;
    readonly canEditWeight?: boolean;
    readonly canEditPageCount?: boolean;
}

export class CommunicationsPermissions {
    public static DIGITAL_MARKETINGS_TAB_HASH = '#rights-digital-marketings';
    public static DIGITAL_MARKETINGS_KEY = 'COMMS_DIGITAL_MARKETINGS_KEY';
    public static DIGITAL_MARKETINGS_NAME = 'DigitalMarketings';

    public static PARTNERSHIPS_TAB_HASH = '#rights-partnerships';
    public static PARTNERSHIPS_KEY = 'COMMS_PARTNERSHIPS_KEY';
    public static PARTNERSHIPS_NAME = 'Partnerships';

    public static LEAFLETS_TAB_HASH = '#rights-leaflets';
    public static LEAFLETS_KEY = 'COMMS_CONTENT_LEAFLETS_KEY';
    public static LEAFLETS_NAME = 'Leaflets';

    public static DigitalMarketings: AdminPermissionsOnModule = {};
    public static Partnerships: AdminPermissionsOnModule = {};
    public static Leaflets: AdminPermissionsOnModule = {};

    //#region //* PERMISSION TABS

    public static getDigitalMarketingsTab = (): TabPermissionItem => ({
        hash: CommunicationsPermissions.DIGITAL_MARKETINGS_TAB_HASH,
        label: <FormattedMessage id="digitalMarketings.permissions.tab.title" />,
        getIcon: () => useIdelinkTheme().digitalMarketings.permissionIcon,
        getIconSelected: () => useIdelinkTheme().digitalMarketings.permissionSelectedIcon,
    });

    public static getPartnershipsTab = (): TabPermissionItem => ({
        hash: CommunicationsPermissions.PARTNERSHIPS_TAB_HASH,
        label: <FormattedMessage id="partnerships.permissions.tab.title" />,
        getIcon: () => useIdelinkTheme().partnerships.permissionIcon,
        getIconSelected: () => useIdelinkTheme().partnerships.permissionSelectedIcon,
    });

    public static getLeafletsTab = (): TabPermissionItem => ({
        hash: CommunicationsPermissions.LEAFLETS_TAB_HASH,
        label: <FormattedMessage id="leaflets.permissions.tab.title" />,
        getIcon: () => useIdelinkTheme().leaflets.permissionIcon,
        getIconSelected: () => useIdelinkTheme().leaflets.permissionSelectedIcon,
    });

    public static getCommunicationsTabs = (): Array<TabPermissionItem> => {
        return [
            CommunicationsPermissions.getDigitalMarketingsTab(),
            CommunicationsPermissions.getPartnershipsTab(),
            CommunicationsPermissions.getLeafletsTab(),
        ];
    };

    //#endregion

    //#region //* ADMIN PERMISSIONS

    public static initCommunicationsAdminPermissions = (perms: Array<AdminPermission> = []) => {
        const toAdmin = (moduleKey: string) => {
            const moduleName = getApiPermissionName(moduleKey);
            const permsOnModule = AdminPermissions.filter(perms, moduleName);
            const adminPermissions = AdminPermissions.toPermissions(permsOnModule);
            PermissionsAbilityHelper.logPermissions(moduleName, adminPermissions);
            return adminPermissions;
        };

        CommunicationsPermissions.DigitalMarketings = toAdmin(
            CommunicationsPermissions.DIGITAL_MARKETINGS_KEY
        );
        CommunicationsPermissions.Partnerships = toAdmin(
            CommunicationsPermissions.PARTNERSHIPS_KEY
        );
        CommunicationsPermissions.Leaflets = toAdmin(CommunicationsPermissions.LEAFLETS_KEY);
    };

    //#endregion

    //#region //* CONTENT PERMISSIONS

    public static toDefaultPermissions = (): UserPermissionsOnContent => {
        const rawPermissions: UserPermissionsOnContent =
            CommunicationsPermissions.toUserPermissionsOnCommunicationItem(ItemState.Draft);
        const permissions: { [field: string]: boolean } = {};
        Object.keys(rawPermissions).forEach((key) => (permissions[key] = true));
        return permissions as UserPermissionsOnContent;
    };

    public static toUserPermissionsOnCommunicationItem = (
        contentState: ItemState,
        contentPermission?: ContentPermission
    ): UserPermissionsOnCommunicationItem => {
        const canEditExtraInfo =
            (contentState === ItemState.Draft &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission)) ||
            (contentState === ItemState.Unpublished &&
                UserPermissions.has(PermissionType.ContentReadResults, contentPermission));

        return {
            ...UserPermissions.toUserPermissionsOnContent(contentState, contentPermission),
            canSave:
                (contentState === ItemState.Draft &&
                    UserPermissions.has(PermissionType.ContentEditDraft, contentPermission)) ||
                (contentState === ItemState.Published &&
                    UserPermissions.has(PermissionType.ContentEditPublished, contentPermission)) ||
                (contentState === ItemState.Unpublished &&
                    UserPermissions.has(PermissionType.ContentEditPublished, contentPermission)) ||
                (contentState === ItemState.Finished &&
                    UserPermissions.has(PermissionType.ContentEditPublished, contentPermission)),

            canEditPdf: UserPermissions.canEdit(contentState, contentPermission),
            canEditPrice: canEditExtraInfo,
            canEditBeforePromoPrice: canEditExtraInfo,
            canEditMinimalCount: canEditExtraInfo,
            canEditWeight: canEditExtraInfo,
            canEditPageCount: canEditExtraInfo,
        };
    };

    //#endregion
}
